// src/components/forms/fieldsConfig/fournisseurFields.js

// Champs pour la création d'un fournisseur avec arrays d'emails et téléphones
export const fournisseurCreationFields = [
  { 
    name: 'nom', 
    label: 'Nom du fournisseur', 
    type: 'text', 
    required: true,
    placeholder: 'Nom complet du fournisseur',
    validate: (value) => !value ? 'Le nom est obligatoire' : '',
    col: 12
  },
  { 
    name: 'RefFournisseur', 
    label: 'Référence fournisseur', 
    type: 'text', 
    placeholder: 'REF-001',
    helpText: 'Référence interne optionnelle',
    col: 6
  },
  { 
    name: 'emails', 
    label: 'Emails', 
    type: 'emails-array', // Type personnalisé pour array d'emails
    placeholder: 'contact@fournisseur.com',
    helpText: 'Ajoutez plusieurs emails séparés par des virgules',
    validate: (value) => {
      if (value && typeof value === 'string') {
        const emails = value.split(',').map(email => email.trim()).filter(email => email);
        for (const email of emails) {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            return `Email invalide: ${email}`;
          }
        }
      }
      return '';
    },
    col: 6
  },
  { 
    name: 'primaryEmail', 
    label: 'Email principal', 
    type: 'email',
    placeholder: 'email.principal@fournisseur.com',
    helpText: 'L\'email principal de contact',
    validate: (value) => {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'Email principal invalide';
      }
      return '';
    },
    col: 6
  },
  { 
    name: 'telephones', 
    label: 'Téléphones', 
    type: 'phones-array', // Type personnalisé pour array de téléphones
    placeholder: '01 23 45 67 89, 06 12 34 56 78',
    helpText: 'Ajoutez plusieurs numéros séparés par des virgules',
    col: 6
  },
  { 
    name: 'primaryTelephone', 
    label: 'Téléphone principal', 
    type: 'tel', 
    placeholder: '01 23 45 67 89',
    col: 6
  },
  { 
    name: 'street', 
    label: 'Adresse', 
    type: 'text', 
    placeholder: 'Rue et numéro',
    col: 6
  },
  { 
    name: 'ville', // Changé de 'city' à 'ville' pour correspondre au backend
    label: 'Ville', 
    type: 'text', 
    placeholder: 'Ville',
    col: 4
  },
  { 
    name: 'zip', 
    label: 'Code postal', 
    type: 'text', 
    placeholder: '75000',
    col: 4
  },
  { 
    name: 'state', 
    label: 'Région/Département', 
    type: 'text', 
    placeholder: 'Ile-de-France',
    col: 4
  },
  { 
    name: 'pays', 
    label: 'Pays', 
    type: 'text', 
    defaultValue: 'France',
    col: 6
  }
];

// Champs pour la mise à jour d'un fournisseur
export const fournisseurUpdateFields = [...fournisseurCreationFields];

// Fonction pour formater les données fournisseur
export const formatFournisseurData = (formData) => {
  const formattedData = {
    nom: formData.nom?.trim(),
    ville: formData.ville?.trim(), // Changé de 'city' à 'ville'
    street: formData.street?.trim(),
    zip: formData.zip?.trim(),
    state: formData.state?.trim(),
    pays: formData.pays?.trim() || 'France',
    primaryEmail: formData.primaryEmail?.trim() || undefined,
    primaryTelephone: formData.primaryTelephone?.trim() || undefined
  };

  // Gérer la référence fournisseur
  if (formData.RefFournisseur?.trim()) {
    formattedData.RefFournisseur = formData.RefFournisseur.trim().toUpperCase();
  }

  // Gérer le tableau d'emails
  if (formData.emails) {
    if (typeof formData.emails === 'string') {
      // Si c'est une chaîne, convertir en tableau
      formattedData.emails = formData.emails
        .split(',')
        .map(email => email.trim())
        .filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    } else if (Array.isArray(formData.emails)) {
      formattedData.emails = formData.emails
        .map(email => email.trim())
        .filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    }
  }

  // Gérer le tableau de téléphones
  if (formData.telephones) {
    if (typeof formData.telephones === 'string') {
      // Si c'est une chaîne, convertir en tableau
      formattedData.telephones = formData.telephones
        .split(',')
        .map(phone => phone.trim())
        .filter(phone => phone);
    } else if (Array.isArray(formData.telephones)) {
      formattedData.telephones = formData.telephones
        .map(phone => phone.trim())
        .filter(phone => phone);
    }
  }

  // Si aucun email principal n'est défini mais qu'il y a des emails, prendre le premier
  if (!formattedData.primaryEmail && formattedData.emails && formattedData.emails.length > 0) {
    formattedData.primaryEmail = formattedData.emails[0];
  }

  // Si aucun téléphone principal n'est défini mais qu'il y a des téléphones, prendre le premier
  if (!formattedData.primaryTelephone && formattedData.telephones && formattedData.telephones.length > 0) {
    formattedData.primaryTelephone = formattedData.telephones[0];
  }

  return formattedData;
};

// Fonction pour pré-remplir les données fournisseur
export const prepareFournisseurFormData = (fournisseur) => {
  if (!fournisseur) return {};
  
  return {
    nom: fournisseur.nom || '',
    RefFournisseur: fournisseur.RefFournisseur || '',
    emails: Array.isArray(fournisseur.emails) ? fournisseur.emails.join(', ') : fournisseur.emails || '',
    telephones: Array.isArray(fournisseur.telephones) ? fournisseur.telephones.join(', ') : fournisseur.telephones || '',
    primaryEmail: fournisseur.primaryEmail || '',
    primaryTelephone: fournisseur.primaryTelephone || '',
    street: fournisseur.street || '',
    ville: fournisseur.ville || '', // Changé de 'city' à 'ville'
    zip: fournisseur.zip || '',
    state: fournisseur.state || '',
    pays: fournisseur.pays || 'France'
  };
};

// Fonction de validation
export const validateFournisseurForm = (formData) => {
  const errors = {};
  
  if (!formData.nom?.trim()) {
    errors.nom = 'Le nom du fournisseur est obligatoire';
  }
  
  // Validation des emails
  if (formData.emails) {
    const emailList = typeof formData.emails === 'string' 
      ? formData.emails.split(',').map(email => email.trim()).filter(email => email)
      : formData.emails;
    
    for (const email of emailList) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.emails = `Email invalide: ${email}`;
        break;
      }
    }
  }
  
  // Validation de l'email principal
  if (formData.primaryEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryEmail)) {
    errors.primaryEmail = 'Email principal invalide';
  }
  
  return errors;
};

export default {
  creation: fournisseurCreationFields,
  update: fournisseurUpdateFields,
  format: formatFournisseurData,
  prepare: prepareFournisseurFormData,
  validate: validateFournisseurForm
};